import React from "react";
import Dialog from "@mui/material/Dialog";
import Button from "../../common/Button";
import { Box, Grid, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useForm } from "react-hook-form";
import { CheckCircleOutline, Close } from "@mui/icons-material";
import CustomDatePicker from "../../common/CustomDatePicker";
import { AddCommissionRequest } from "../../../redux/slice/saleDetails/SaleDetailsSlice";
import dayjs from "dayjs";
import { useModalStyles } from "../../common/useModalStyles";
import { FieldLabel } from "../../../utils/Utility";

export type CommissionConfirmationData = {
    effectiveDate: Date | null;
}

export type ButtonComponentProps = {
    name: string;
    color:
    | "info"
    | "success"
    | "warning"
    | "primary"
    | "secondary"
    | "error"
    | undefined;
    onClick: (data?: AddCommissionRequest) => void;
    icon?: React.ReactNode;
    type?: "button" | "submit";
};

interface CommissionPayoutPopupProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (data: AddCommissionRequest) => void;
    title?: string;
    commissionAmountLabel?: string;
    commissionObject: AddCommissionRequest;
    currency?: string;
    buttons?: ButtonComponentProps[];
    /** Full expected commission for this payment level */
    expectedAmount?: number;
    /** Amount already paid/received for this payment level */
    alreadyPaidAmount?: number;
    invoiceNumber?: string;
    orderNumber?: string;
    zIndex?: number;
}

const commissionConfirmationInitialState: CommissionConfirmationData = {
    effectiveDate: null,
};

const CommissionPayoutPopup: React.FC<CommissionPayoutPopupProps> = ({
    open,
    onClose,
    onConfirm,
    title = "Service Fee Confirmation",
    commissionAmountLabel = "Service Fee Amount",
    commissionObject,
    currency = "$",
    buttons,
    expectedAmount,
    alreadyPaidAmount = 0,
    invoiceNumber,
    orderNumber,
    zIndex,
}) => {
    const { control, handleSubmit, reset, getValues } = useForm<CommissionConfirmationData>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {
        tokens,
        paperSx,
        headerSx,
        iconBoxSx,
        closeIconSx,
        bodySx,
        footerSx,
        datepickerWrapSx,
        primaryBtnSx,
        cancelBtnSx,
    } = useModalStyles();

    const remainingAmount = Math.max(
        0,
        Math.round((commissionObject.commissionReceivedAmount || 0) * 100) / 100
    );
    const hasPartialPayment =
        Number(alreadyPaidAmount) > 0 &&
        expectedAmount !== undefined &&
        Number(expectedAmount) > Number(alreadyPaidAmount);

    React.useEffect(() => {
        if (open) {
            reset(commissionConfirmationInitialState);
        }
    }, [open, reset]);

    const onSubmit = (data: CommissionConfirmationData) => {
        const updatedCommissionObject: AddCommissionRequest = {
            ...commissionObject,
            // Always submit only the remaining balance to pay
            commissionReceivedAmount: remainingAmount,
            payoutDate: data.effectiveDate ? dayjs(data.effectiveDate).format("MM/DD/YYYY") : "",
        };

        onConfirm(updatedCommissionObject);
        reset(commissionConfirmationInitialState);
        onClose();
    };

    const handleClose = () => {
        reset(commissionConfirmationInitialState);
        onClose();
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency === '$' ? 'USD' : 'USD',
            minimumFractionDigits: 2,
        }).format(amount);
    };

    return (
        <Dialog
            open={open}
            keepMounted
            onClose={handleClose}
            aria-describedby="commission-confirmation-dialog"
            onClick={(event) => {
                event.stopPropagation();
            }}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            sx={zIndex !== undefined ? { zIndex } : undefined}
            PaperProps={{ sx: paperSx(isMobile) }}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={headerSx}>
                    <Box sx={iconBoxSx(tokens.accent)}>
                        <CheckCircleOutline sx={{ fontSize: "1.2rem", color: tokens.accent }} />
                    </Box>
                    <Box sx={{ flex: 1, pr: 4 }}>
                        <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                            {title}
                        </Typography>
                        <Typography id="commission-confirmation-dialog" sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                            Confirm payout amount and paid date.
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small" sx={closeIconSx} aria-label="Close">
                        <Close sx={{ fontSize: "1rem" }} />
                    </IconButton>
                </Box>
                <Box sx={bodySx}>
                    <Box>
                        <Grid container spacing={3}>
                            {invoiceNumber || orderNumber ? (
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 2,
                                            px: 0.5,
                                        }}
                                    >
                                        {invoiceNumber ? (
                                            <Box sx={{ flex: 1, minWidth: 140 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Invoice #
                                                </Typography>
                                                <Typography sx={{ fontWeight: 700, color: tokens.textPrimary }}>
                                                    {invoiceNumber}
                                                </Typography>
                                            </Box>
                                        ) : null}
                                        {orderNumber ? (
                                            <Box sx={{ flex: 1, minWidth: 140 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Order #
                                                </Typography>
                                                <Typography sx={{ fontWeight: 700, color: tokens.textPrimary }}>
                                                    {orderNumber}
                                                </Typography>
                                            </Box>
                                        ) : null}
                                    </Box>
                                </Grid>
                            ) : null}
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        p: 2,
                                        borderRadius: '8px',
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.05)'
                                            : 'rgba(0, 0, 0, 0.05)',
                                        border: `1px solid ${theme.palette.divider}`,
                                        mb: 1
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mb: 1 }}
                                    >
                                        {commissionAmountLabel}
                                    </Typography>
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 'bold',
                                            color: theme.palette.success.main
                                        }}
                                    >
                                        {formatCurrency(remainingAmount)}
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={12}>
                                <Box>
                                    <FieldLabel label="Paid Date" required tokens={tokens} />
                                    <Box sx={datepickerWrapSx(false)}>
                                        <CustomDatePicker
                                            control={control}
                                            controllerName="effectiveDate"
                                            label=""
                                            rules={{
                                                required: "Paid date is required.",
                                            }}
                                            fullWidth
                                            popperZIndex={(zIndex ?? 1300) + 100}
                                        />
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
                <Box sx={footerSx}>
                    <Grid container justifyContent="flex-end" sx={{ flexWrap: "wrap" }} spacing={1}>
                            {buttons ? (
                                buttons.map((button, index) => (
                                    <Grid item xs={12} sm="auto" key={index}>
                                        <Button
                                            type={button.type}
                                            color={button.color}
                                            onClick={button.type === "submit" ? undefined : () => {
                                                if (button.type !== "submit") {
                                                    const formData = getValues();
                                                    const updatedCommissionObject: AddCommissionRequest = {
                                                        ...commissionObject,
                                                        commissionReceivedAmount: remainingAmount,
                                                        payoutDate: formData.effectiveDate? dayjs(formData.effectiveDate).format("MM/DD/YYYY") : ""
                                                    };
                                                    button.onClick(updatedCommissionObject);
                                                }
                                            }}
                                            startIcon={button.icon}
                                            fullWidth
                                        >
                                            {button.name}
                                        </Button>
                                    </Grid>
                                ))
                            ) : (
                                <>
                                    <Grid item xs={12} sm="auto">
                                        <Box
                                            component="button"
                                            type="submit"
                                            sx={{
                                                ...primaryBtnSx,
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: 0.8,
                                                px: 3,
                                                py: 1.2,
                                                border: "none",
                                                cursor: "pointer",
                                                color: "#fff",
                                                width: "100%",
                                            }}
                                        >
                                            <CheckCircleOutline sx={{ fontSize: "1.1rem" }} />
                                            Confirm
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm="auto">
                                        <Box
                                            component="button"
                                            type="button"
                                            onClick={handleClose}
                                            sx={{
                                                ...cancelBtnSx,
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: 0.8,
                                                px: 3,
                                                py: 1.2,
                                                border: "1px solid",
                                                cursor: "pointer",
                                                width: "100%",
                                            }}
                                        >
                                            Cancel
                                        </Box>
                                    </Grid>
                                </>
                            )}
                    </Grid>
                </Box>
            </form>
        </Dialog>
    );
};

export default CommissionPayoutPopup;